/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Config,
  EditableComponent,
  MappedComponentProperties,
  ResponsiveGrid,
} from '@adobe/aem-react-editable-components';
import { HeroBannerProps, heroBannerImageType } from './HeroBanner.types';
import { StyledHeroBanner } from './HeroBanner.styles';
import { Image, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { constants, internetBarHeight, internetBarMobileHeight } from '../../modules/utils/constants/constants';
import { InternetBar } from '../InternetBar';
import { HeroBannerImage } from './components/HeroBannerImage';

const { NEXT_PUBLIC_AEM_SITE } = process.env;

export const HeroBanner: FC<HeroBannerProps> = pageProps => {
  const [heroBannerImage, setHeroBannerImage] = useState<heroBannerImageType | null>(null);
  const internetBarContainerRef = useRef(null);
  const heroBannerContainerRef = useRef(null);
  const isTabletAndAboveViewPort = useCheckBreakpoint('viewportM');

  const resizeImage = () => {
    const internetBarContainerHeight =
      (internetBarContainerRef?.current as unknown as HTMLElement)?.querySelector('.internet-bar-container')
        ?.clientHeight ?? 0;
    const heroBannerContainerHeight = (heroBannerContainerRef?.current as unknown as HTMLElement)?.clientHeight ?? 0;
    const heroBannerImageStyle = (heroBannerContainerRef?.current as unknown as HTMLElement)?.querySelector(
      'picture img'
    );
    const updatedImageSpacing = isTabletAndAboveViewPort ? internetBarHeight : internetBarMobileHeight;
    if (
      heroBannerContainerHeight <= internetBarContainerHeight + updatedImageSpacing / 2 &&
      heroBannerContainerRef?.current
    ) {
      (heroBannerContainerRef?.current as unknown as HTMLElement).style.height = `${
        internetBarContainerHeight + updatedImageSpacing
      }px`;

      if (heroBannerImageStyle) {
        (heroBannerImageStyle as HTMLElement).style.height = `${internetBarContainerHeight + updatedImageSpacing}px`;
      }
    }
  };

  //Method for returning Responsive Grid for showing parsys in Author mode
  const InternetBarContainer = () => {
    return (
      <div data-testid="brand-container-component">
        <ResponsiveGrid
          gridClassNames={`col-12`}
          columnClassNames={{
            responsivegrid: `col-12`,
          }}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          pagePath={pageProps?.pagePath}
          itemPath={`${pageProps?.itemPath}/container-0`}
          config={{
            isEmpty: () => true,
            resourceType: 'mi-aem-common-spa/components/container',
          }}
        />
      </div>
    );
  };

  //Method for returning components to be rendering at end point URL

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const InternetBarContainerWrapper = (cardName: string, jsonData: any) => {
    const returnable = [];
    // eslint-disable-next-line no-prototype-builtins
    if (jsonData?.hasOwnProperty(cardName)) {
      const card = jsonData[cardName];
      const cardItems = card[':items'];
      for (const itemKey in cardItems) {
        // eslint-disable-next-line no-prototype-builtins
        if (cardItems?.hasOwnProperty(itemKey)) {
          const item = cardItems[itemKey];
          returnable.push(<InternetBar {...item} key={itemKey} resizeImage={resizeImage} />);
        }
      }
      return <div data-testid="brand-container-component-wrapper">{returnable}</div>;
    }
    return null;
  };

  const showInternetBarContainer = pageProps.isAuthorMode ? (
    <div>{Array.from({ length: 1 }, _ => InternetBarContainer())}</div>
  ) : (
    <div>
      {pageProps?.cqItemsOrder && InternetBarContainerWrapper(pageProps?.cqItemsOrder?.[0], pageProps?.cqItems)}
    </div>
  );

  const onHeroBannerImageDataFetched = (imageData: any) => {
    if (imageData) {
      setHeroBannerImage({ ...imageData });
    }
  };

  useLayoutEffect(() => {
    if (heroBannerImage) {
      resizeImage();
    }
  }, [heroBannerImage]);

  useEffect(() => {
    const handleResize = () => {
      resizeImage();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <StyledHeroBanner
      data-testid="hero-banner"
      data-component-name="o-ppv5-hero-banner"
      className="m-container-fullbleed"
    >
      <div className="hero-component">
        <div className="hero-component-container" ref={heroBannerContainerRef}>
          {!heroBannerImage && (
            <HeroBannerImage
              onHeroBannerImageDataFetched={onHeroBannerImageDataFetched}
              isAuthorMode={pageProps?.isAuthorMode}
              heroBannerPageProps={pageProps}
              heroBannerImage={heroBannerImage}
            />
          )}
          {heroBannerImage && (
            <Image
              defaultImageURL={constants.BASE_IMG_URL}
              altText={heroBannerImage.altText}
              dynamic={true}
              renditions={heroBannerImage?.renditions ?? []}
              loading="eager"
              customClass="hero-component-img col-12 p-0 loaded"
            ></Image>
          )}
          <div className="hero-component__internetbar" ref={internetBarContainerRef}>
            {showInternetBarContainer}
          </div>
        </div>
      </div>
    </StyledHeroBanner>
  );
};

export const HeroBannerConfig = {
  emptyLabel: 'HeroBanner',
  isEmpty: (_props: MappedComponentProperties) => false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/hero-banner`,
} as Config<MappedComponentProperties>;

export const HeroBannerEditable = (props: HeroBannerProps) => (
  <EditableComponent config={HeroBannerConfig} {...props}>
    <HeroBanner {...props} />
  </EditableComponent>
);
